import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Wizard,
  Grid,
  Container,
  Header,
  Button,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { FilePreview } from "components/file-preview/FilePreview";
import { useGetScanjobQuery } from "pages/draft-overview/endpoints";
import { useUpdateScanJobMutation } from "pages/drafts/endpoint";
import { useDownloadUrlQuery } from "components/documents-table/endpoints";
import CreateAuditForm from "components/create-audit-form/CreateAuditForm";
import { AuditFormRef } from "components/create-audit-form/definitions";
import {
  useCreateAuditMutation,
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import { useUpdateAuditMutation } from "components/edit-audit-form/endpoints";
import { useCreateTasksMutation } from "components/task-group/endpoints";
import TaskSuggestions from "components/task-suggestions/TaskSuggestions";
import Loader from "components/loader";
import { useAppSelector, useAppDispatch } from "app/store";
import { useNotifications } from "features/notifications";
import { usePageHeader } from "features/page-header";
import {
  setTaskData,
  clearTaskData,
  useGetTaskData,
} from "features/draft-wizard";
import { useLocalStorage } from "hooks/useLocalStorage";
import { DraftsWizardProps } from "./definitions";
import { z } from "zod";
import { getAuditName } from "components/audit-name/AuditName";
import { AuditNameProps } from "components/audit-name/definitions";
import { determineJurisdictionLevel } from "utils/jurisdictionUtils";
import {
  toUnixEpochTime,
  getFileNameWithoutExtension,
} from "utils/date-format";
import { generateDynamicAttributesDataMap } from "utils/dynamicAttributesUtils";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { getEntityCodeFromLongName } from "features/ui-content/utils";
import { getAuditErrorMessage } from "components/create-audit-form/utils";
import { TaskSuggestionGroup } from "components/task-suggestions/definitions";
import { createTasksPayloads } from "utils/taskTransformUtils";
import { GaiaConstants } from "utils/gaia-constants";

const DraftsWizard: React.FC<DraftsWizardProps> = ({
  visible = true,
  onDismiss,
  scanJobId,
  showFilePreview = true,
  category,
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [showWizard, setShowWizard] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFilePreviewVisible, setIsFilePreviewVisible] =
    useState(showFilePreview);
  const auditFormRef = useRef<AuditFormRef>(null);

  const { shortId } = useParams<{ shortId: string }>();
  const dispatch = useAppDispatch();
  const currentScanJobId = scanJobId || shortId || "";

  const localStorageKey = `draft-wizard-step1-${currentScanJobId}`;
  const [storedFormData, setStoredFormData] = useLocalStorage<any>(
    localStorageKey,
    null
  );

  const taskData = useGetTaskData(currentScanJobId);
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { updatePageHeader } = usePageHeader();
  const [createAudit] = useCreateAuditMutation();
  const [updateAudit] = useUpdateAuditMutation();
  const [createTasks] = useCreateTasksMutation();
  const [registerDocumentCall] = useRegisterDocumentMutation();
  const [updateScanJobCall] = useUpdateScanJobMutation();
  const [uploadUrlCall] = useUploadUrlMutation();

  const teamName = useAppSelector((state) => state.audits.teamName);
  const auditSchema = useAppSelector(
    (state) => state.teamSettings.auditSchemas.auditSchema
  );
  const auditCustomAttributes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema
        ?.audit
  );
  const data: any = useAppSelector((state) => state.uiContent);

  const {
    data: scanJobData,
    isLoading: scanJobLoading,
    isError,
  } = useGetScanjobQuery(
    { shortId: (scanJobId || shortId)! },
    { refetchOnMountOrArgChange: true, skip: !(scanJobId || shortId) }
  );

  const {
    data: downloadUrlData,
    isLoading: downloadUrlLoading,
    refetch: refetchDownloadUrl,
  } = useDownloadUrlQuery(
    { documentId: (scanJobId || shortId)!, preview: true },
    {
      skip: !(scanJobId || shortId),
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0,
    }
  );

  useEffect(() => {
    if (scanJobData?.scanJob) {
      setShowWizard(true);
    }
  }, [scanJobData]);

  useEffect(() => {
    if (showWizard) {
      updatePageHeader({ hideTitle: true });
    }
    return () => {
      updatePageHeader({ hideTitle: false });
    };
  }, [showWizard]);

  useEffect(() => {
    return () => {
      if (currentScanJobId) {
        dispatch(clearTaskData({ scanJobId: currentScanJobId }));
        localStorage.removeItem(localStorageKey);
      }
    };
  }, [currentScanJobId, dispatch, localStorageKey]);

  const isTaskOnlyMode = scanJobData?.scanJob?.category === "Task";

  const renderFilePreview = () => {
    if (scanJobLoading || downloadUrlLoading) {
      return <div>Loading file preview...</div>;
    }
    if (scanJobData?.scanJob && downloadUrlData?.url) {
      return (
        <FilePreview
          key={`${scanJobData.scanJob.fileName}-${downloadUrlData.url}`}
          type="pdf"
          fileName={scanJobData.scanJob.fileName}
          presignedUrl={downloadUrlData.url}
        />
      );
    }
    return <div>No file preview available</div>;
  };

  const handleFormSubmit = async (formData: any) => {
    if (activeStepIndex === 0) {
      setStoredFormData(formData);
      setActiveStepIndex(1);
      return;
    }
    try {
      await createAudit(processAuditData(formData)).unwrap();
      addNotification({
        type: "success",
        header: "Audit created successfully",
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });
      navigate("/audits");
      onDismiss?.();
    } catch (e: any) {
      const errorMessage = getAuditErrorMessage(
        e,
        "Failed to create the audit"
      );

      addNotification({
        type: "error",
        header: errorMessage,
        dismissible: true,
        timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
      });
    }
  };

  const processAuditData = (audit: z.infer<typeof auditSchema>) => {
    const auditNameProps: AuditNameProps = {
      country: audit.country,
      governmentEntity: audit.governmentEntity,
      localJurisdiction: audit.localJurisdiction,
      entity: audit.entity,
      taxType: audit.taxType,
      auditStartDateString: audit.auditStartDateString,
      auditEndDateString: audit.auditEndDateString,
      auditNameSuffix: audit.auditNameSuffix,
      entityCode: audit.entityCode,
    };
    audit.auditName = getAuditName(auditNameProps);
    audit.teamName = teamName;
    audit.jurisdictionLevel = determineJurisdictionLevel(
      audit.governmentEntity,
      audit.localJurisdiction
    );
    audit.auditStartDate = toUnixEpochTime(audit.auditStartDateString);
    audit.auditEndDate = toUnixEpochTime(audit.auditEndDateString);
    audit.auditInitiationDate = toUnixEpochTime(
      audit.auditInitiationDateString
    );
    audit.auditCloseDate = toUnixEpochTime(audit.auditCloseDateString);
    audit.statuteOfLimitations = toUnixEpochTime(
      audit.statuteOfLimitationsString
    );
    audit.statusActionOwner = "Amazon";
    audit.templateId = audit.template;
    audit.entityCode = getEntityCodeFromLongName(audit.entity, data);

    if (auditCustomAttributes) {
      audit.customAttributesDataMap = generateDynamicAttributesDataMap(
        auditCustomAttributes,
        audit[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]
      );
    }
    return audit;
  };

  if (scanJobLoading) {
    return <Loader loadingText="Loading draft..." />;
  }

  if (isError || !scanJobData?.scanJob) {
    return null;
  }

  if (!showWizard) {
    return <Loader loadingText="Loading draft..." />;
  }

  const allSteps = [
    {
      title: "Details",
      description: (
        <>
          <br />
          Audit details have been filled based on your uploaded file:{" "}
          {scanJobData?.scanJob?.fileName || "uploaded file"}
        </>
      ),
      content: (
        <SpaceBetween size="m">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              iconName={isFilePreviewVisible ? "view-horizontal" : "view-full"}
              onClick={() => setIsFilePreviewVisible(!isFilePreviewVisible)}
            >
              {isFilePreviewVisible ? "Hide" : "Show"} File Preview
            </Button>
          </SpaceBetween>
          <Grid
            gridDefinition={
              isFilePreviewVisible
                ? [{ colspan: 6 }, { colspan: 6 }]
                : [{ colspan: 12 }]
            }
          >
            {isFilePreviewVisible && (
              <Container header={<Header variant="h2">File Preview</Header>}>
                {renderFilePreview()}
              </Container>
            )}
            <CreateAuditForm
              ref={auditFormRef}
              wizardMode={true}
              mode="draft"
              shortId={shortId}
              defaultOwner={scanJobData?.scanJob?.createdBy}
              onFormSubmit={handleFormSubmit}
            />
          </Grid>
        </SpaceBetween>
      ),
    },
    {
      title: "Tasks",
      description:
        "Based on the uploaded file, we created following task groups. If you wish to skip task creation, click on 'Remove all'.",
      content: (
        <SpaceBetween size="m">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              iconName={isFilePreviewVisible ? "view-horizontal" : "view-full"}
              onClick={() => setIsFilePreviewVisible(!isFilePreviewVisible)}
            >
              {isFilePreviewVisible ? "Hide" : "Show"} File Preview
            </Button>
          </SpaceBetween>
          <Grid
            gridDefinition={
              isFilePreviewVisible
                ? [{ colspan: 6 }, { colspan: 6 }]
                : [{ colspan: 12 }]
            }
          >
            {isFilePreviewVisible && (
              <Container header={<Header variant="h2">File Preview</Header>}>
                {renderFilePreview()}
              </Container>
            )}
            <Container>
              <TaskSuggestions
                shortId={scanJobId || shortId}
                auditData={scanJobData}
                teamName={teamName}
                templateId={
                  storedFormData?.templateId || storedFormData?.template
                }
                onTaskDataChange={(newTaskData) => {
                  dispatch(
                    setTaskData({
                      scanJobId: currentScanJobId,
                      taskData: newTaskData,
                    })
                  );
                }}
                initialTaskData={taskData}
                category={category}
              />
            </Container>
          </Grid>
        </SpaceBetween>
      ),
    },
  ];

  const steps = isTaskOnlyMode ? [allSteps[1]] : allSteps;

  const handleNavigate = (detail: {
    requestedStepIndex: number;
    reason: string;
  }) => {
    if (
      activeStepIndex === 0 &&
      detail.requestedStepIndex === 1 &&
      auditFormRef.current
    ) {
      auditFormRef.current.submitForm();
      return;
    }

    if (activeStepIndex === 0 && auditFormRef.current) {
      const formData = auditFormRef.current.getFormData();
      if (formData) {
        setStoredFormData({
          ...formData,
          templateId: formData.templateId,
        });
      }
    }

    refetchDownloadUrl();
    setActiveStepIndex(detail.requestedStepIndex);
  };

  const handleWizardSubmit = async () => {
    const isLastStep = activeStepIndex === steps.length - 1;

    if (isLastStep) {
      if (isSubmitting) {
        return;
      }
      setIsSubmitting(true);

      let formDataToUse = storedFormData;

      if (auditFormRef.current) {
        const currentFormData = auditFormRef.current.getFormData();
        if (currentFormData) {
          formDataToUse = currentFormData;
          setStoredFormData(currentFormData);
        }
      }

      if (!formDataToUse) {
        addNotification({
          type: "error",
          header: "Missing audit information",
          content:
            "Please complete the audit form in step 1 before creating the audit.",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
        setActiveStepIndex(0);
        setIsSubmitting(false);
        return;
      }

      let createdAuditId: string | null = null;
      let createdAuditData: any = null;

      try {
        const processedAuditData = processAuditData(formDataToUse);
        const auditResponse = await createAudit(processedAuditData).unwrap();

        createdAuditId = auditResponse.auditId;
        createdAuditData = auditResponse;

        if (!createdAuditId) {
          throw new Error("Audit ID not found in response");
        }

        if (scanJobData?.scanJob?.fileName && downloadUrlData?.url) {
          try {
            const uploadRequest = {
              prefix: `${GaiaConstants.AUDIT_FILES_PREFIX}/${createdAuditId}`,
              fileName: scanJobData.scanJob.fileName,
              teamName: teamName,
              securityLevel: "Normal",
            };
            const uploadResponse = await uploadUrlCall(uploadRequest).unwrap();
            const { data } = await refetchDownloadUrl();
            const originalFileResponse = await fetch(
              data?.url || downloadUrlData.url
            );
            const fileBlob = await originalFileResponse.blob();
            await fetch(uploadResponse.url, {
              method: "PUT",
              body: fileBlob,
              headers: {
                "x-amz-expected-bucket-owner": uploadResponse.bucketOwner,
              },
            });
            const s3Prefix = `${GaiaConstants.AUDIT_FILES_PREFIX}/${createdAuditId}/${uploadResponse.uniqueKey}/${scanJobData.scanJob.fileName}`;
            const registerDocumentRequest = {
              parentId: createdAuditId,
              teamName: teamName,
              fileName: getFileNameWithoutExtension(
                scanJobData.scanJob.fileName
              ),
              documentType: "Audit notices",
              securityLevel: "Normal",
              s3Prefix: s3Prefix,
              extentionType: scanJobData.scanJob.extensionType,
              auditId: createdAuditId,
              size: scanJobData.scanJob.size,
            };
            await registerDocumentCall(registerDocumentRequest).unwrap();
          } catch (documentError: any) {
            console.error("Failed to register document:", documentError);
          }
        }
        if (taskData && taskData.length > 0) {
          try {
            const payloads = createTasksPayloads(
              taskData,
              createdAuditId,
              teamName,
              formDataToUse.owner || scanJobData?.scanJob?.createdBy
            );
            for (let i = 0; i < payloads.length; i++) {
              const payload = payloads[i];
              await createTasks(payload).unwrap();
            }
          } catch (taskError: any) {
            try {
              await updateAudit({
                ...createdAuditData,
                auditId: createdAuditId,
                deleted: true,
              }).unwrap();
            } catch (rollbackError: any) {
              console.error("Failed to rollback audit:", rollbackError);
            }
            throw taskError;
          }
        }

        if (shortId) {
          try {
            const updateScanJobRequest = {
              shortId: shortId,
              gaiaObjectId: createdAuditId,
              status: "Review complete",
            };
            await updateScanJobCall(updateScanJobRequest).unwrap();
          } catch (scanJobError) {
            console.error("Failed to update scan job:", scanJobError);
          }
        }

        addNotification({
          type: "success",
          header: "Audit and tasks created successfully",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });

        dispatch(clearTaskData({ scanJobId: currentScanJobId }));
        localStorage.removeItem(localStorageKey);
        navigate("/audits");
        onDismiss?.();
      } catch (e: any) {
        const errorMessage = getAuditErrorMessage(
          e,
          createdAuditId
            ? "Failed to create tasks. Audit has been deleted."
            : "Failed to create the audit"
        );

        addNotification({
          type: "error",
          header: errorMessage,
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleWizardCancel = () => {
    dispatch(clearTaskData({ scanJobId: currentScanJobId }));
    localStorage.removeItem(localStorageKey);
    navigate("/drafts");
    onDismiss?.();
  };

  if (!visible) return null;

  return (
    <Wizard
      i18nStrings={{
        stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
        collapsedStepsLabel: (stepNumber, stepsCount) =>
          `Step ${stepNumber} of ${stepsCount}`,
        skipToButtonLabel: (step, stepNumber) => `Skip to ${step.title}`,
        navigationAriaLabel: "Steps",
        cancelButton: "Cancel",
        previousButton: "Previous",
        nextButton: "Next",
        submitButton: isSubmitting ? "Creating..." : "Create",
        optional: "optional",
      }}
      onNavigate={({ detail }) => !isSubmitting && handleNavigate(detail)}
      activeStepIndex={activeStepIndex}
      steps={steps}
      allowSkipTo={false}
      onCancel={!isSubmitting ? handleWizardCancel : undefined}
      onSubmit={handleWizardSubmit}
      isLoadingNextStep={isSubmitting}
    />
  );
};

export default DraftsWizard;
